import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, BarChart3 } from 'lucide-react';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/ReportsFolder.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';

/* --------------------------------------------------------
   Helpers
-------------------------------------------------------- */
const fmtCurrency = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '-';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

function fmtDateRange(start, end) {
  const toMDY = (d) => {
    if (!d) return '-';
    const [y, m, day] = d.split('-');
    if (!y || !m || !day) return d;
    return `${m}/${day}/${y}`;
  };
  return `${toMDY(start)} - ${toMDY(end)}`;
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

/* ========================================================
   PROFIT BY PROJECT (SUMMARY)
======================================================== */
export default function ProfitByProjectPage() {
  const [report, setReport] = useState(null);
  const [company, setCompany] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allProjects, setAllProjects] = useState([]);

  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* Filters */
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [selectedProjectId, setSelectedProjectId] = useState('all');

  /* UI state */
  const [editingDates, setEditingDates] = useState(false);

  const token = useMemo(() => localStorage.getItem('idToken'), []);
  const userId = useMemo(() => localStorage.getItem('userId'), []);

  const API = useMemo(
    () => API_BASE,
    []
  );

  /* --------------------------------------------------------
     Load Company Settings
  -------------------------------------------------------- */
  useEffect(() => {
    if (!token || !userId) return;

    (async () => {
      try {
        const res = await fetch(`${API}/api/settings/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load settings');
        const json = await res.json();
        setCompany(json?.company || null);
      } catch (err) {
        console.warn('Company settings load failed:', err);
      }
    })();
  }, [API, token, userId]);

  useEffect(() => {
    if (!token || !userId) return;

    (async () => {
      try {
        const res = await fetch(`${API}/api/projects/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Failed to load projects');

        const json = await res.json();
        setAllProjects(Array.isArray(json) ? json : []);
      } catch (err) {
        console.warn('Project list load failed:', err);
      }
    })();
  }, [API, token, userId]);

  /* --------------------------------------------------------
     Load Report (refetch on filter change)
  -------------------------------------------------------- */
  useEffect(() => {
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const qs = new URLSearchParams();
        qs.set('detailed', 'false');
        qs.set('startDate', startDate);
        qs.set('endDate', endDate);
        if (selectedProjectId !== 'all') {
          qs.set('projectId', selectedProjectId);
        }

        const res = await fetch(`${API}/api/reports/profit-by-project?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to load report');
        }

        const json = await res.json();
        setReport(json);
      } catch (err) {
        console.error('ProfitByProject load failed:', err);
        setError('Failed to load profit report');
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, startDate, endDate, selectedProjectId]);

  /* --------------------------------------------------------
     Computed
  -------------------------------------------------------- */
  const projects = Array.isArray(report?.projects) ? report.projects : [];

  const companyName = company?.legalName || company?.companyName || company?.name || 'Company';

  function exportExcel() {
    const headers = ['Project', 'Income', 'Cost of Goods Sold', 'Profit'];
    const rowsForExport = projects.map((p) => [
      p.projectName || '',
      Number(p.totals?.income || 0),
      Number(p.totals?.cogs || 0),
      Number(p.totals?.profit || 0),
    ]);
    rowsForExport.push([
      'Total',
      Number(report.grandTotals?.income || 0),
      Number(report.grandTotals?.cogs || 0),
      Number(report.grandTotals?.profit || 0),
    ]);

    exportRowsToExcel({
      filename: 'profit-by-project.xlsx',
      sheetName: 'Profit by Project',
      headers,
      rows: rowsForExport,
    });
  }

  /* --------------------------------------------------------
     States
  -------------------------------------------------------- */
  if (loading)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              <Loader2 size={32} strokeWidth={2} style={{ animation: 'spin 1s linear infinite', color: '#62794e' }} />
            </div>
            <div className="report-loading-text">Loading profit report…</div>
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              <BarChart3 size={32} strokeWidth={1.5} style={{ color: '#e5484d' }} />
            </div>
            <div className="report-loading-text">{error}</div>
          </div>
        </div>
      </div>
    );
  if (!report)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              <BarChart3 size={32} strokeWidth={1.5} style={{ color: '#9aa0a6' }} />
            </div>
            <div className="report-loading-text">No data found. Try adjusting your date range.</div>
          </div>
        </div>
      </div>
    );

  /* --------------------------------------------------------
     Render
  -------------------------------------------------------- */
  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-sheet">
          <header className="report-sheet-header">
            <h1>{companyName}</h1>
            <h2>Profit by Project</h2>

            {/* Date Range + Pencil */}
            <div className="report-dates">
              {!editingDates ? (
                <>
                  {fmtDateRange(startDate, endDate)}
                  <button
                    className="date-edit-btn"
                    onClick={() => setEditingDates(true)}
                    aria-label="Edit date range"
                    title="Edit date range"
                  >
                    Edit
                  </button>
                </>
              ) : (
                <div className="date-edit-inline">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  <span>-</span>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  <button className="date-save-btn" onClick={() => setEditingDates(false)}>
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Filter by Project (always visible) */}
            <div className="report-filter-row">
              <div className="report-filter-label">Filter by</div>
              <select
                className="report-filter-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="all">Project</option>
                {allProjects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.projectNickname}
                  </option>
                ))}
              </select>

              <button className="report-btn-secondary" onClick={exportExcel}>
                Export Excel
              </button>
            </div>
          </header>

          {/* Table */}
          <div className="financial-report-table-wrap">
            <table className="financial-report-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Income</th>
                  <th>Cost of Goods Sold</th>
                  <th>Profit</th>
                </tr>
              </thead>

              <tbody>
                {projects.map((p) => (
                  <tr key={p.projectId}>
                    <td>{p.projectName}</td>
                    <td>{fmtCurrency(p.totals?.income)}</td>
                    <td>{fmtCurrency(p.totals?.cogs)}</td>
                    <td>{fmtCurrency(p.totals?.profit)}</td>
                  </tr>
                ))}

                <tr className="grand-total">
                  <td>Total</td>
                  <td>{fmtCurrency(report.grandTotals?.income)}</td>
                  <td>{fmtCurrency(report.grandTotals?.cogs)}</td>
                  <td>{fmtCurrency(report.grandTotals?.profit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
